import React, { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Mousewheel } from "swiper/modules";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Clock } from "lucide-react";
import "swiper/css";
import "swiper/css/navigation";
import styles from "./Css/AudioBookCarousel.module.css";
import { useCategories } from "../../hooks/categories/useCategories";
import { useShortAudiosByCategory } from "../../hooks/useHomePageHooks";

const AudioBookCarousel = () => {
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [displayData, setDisplayData] = useState([]);
  const navigate = useNavigate();

  // Get categories
  const { categories: categoriesData, isLoading: isCategoriesLoading } =
    useCategories("Audio");

  // Get short audios by category
  const {
    shortAudiosByCategory,
    isLoading: shortsLoading,
    error: shortsError,
  } = useShortAudiosByCategory(activeCategoryId);

  // Set up categories array
  const [categories, setCategories] = useState([]);

  // Initialize with first category when data is loaded - limit to 5 categories and filter out "Series"
  useEffect(() => {
    if (categoriesData && categoriesData.length > 0) {
      // Filter out the "Series" category
      const filteredCategories = categoriesData.filter(
        category => category.name !== "Series"
      );
      
      // Take only the first 5 categories after filtering
      const limitedCategories = filteredCategories.slice(0, 5);
      setCategories(limitedCategories);

      // Set the first category as active by default
      if (!activeCategory && limitedCategories.length > 0) {
        setActiveCategory(limitedCategories[0]?.id);
        setActiveCategoryId(limitedCategories[0]?.id);
      }
    }
  }, [categoriesData, activeCategory]);

  // Update displayData based on active category
  useEffect(() => {
    if (
      shortAudiosByCategory &&
      shortAudiosByCategory.audios &&
      shortAudiosByCategory.audios.length > 0
    ) {
      setDisplayData(shortAudiosByCategory.audios);
    } else {
      setDisplayData([]);
    }
  }, [shortAudiosByCategory]);

  // Handle category change
  const handleCategoryChange = (categoryId) => {
    // Only take action if selecting a different category
    if (activeCategory !== categoryId) {
      setActiveCategory(categoryId);
      setActiveCategoryId(categoryId);

      // Only clear display data when switching to a different category
      setDisplayData([]);
    }
  };
  // Format duration to minutes
  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return "0min";

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes === 0) {
      return `${remainingSeconds}s`;
    } else if (remainingSeconds === 0) {
      return `${minutes}min`;
    } else {
      return `${minutes}min ${remainingSeconds}s`;
    }
  };

  // Loading state
  const isLoading = shortsLoading;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Short Stories</h2>

        <div className={styles.categories}>
          {isCategoriesLoading ? (
            <p>Loading categories...</p>
          ) : (
            categories.map((category) => (
              <button
                key={category.id}
                className={`${styles.categoryBtn} ${
                  activeCategory === category.id ? styles.active : ""
                }`}
                onClick={() => handleCategoryChange(category.id)}
              >
                {category.name}
              </button>
            ))
          )}
        </div>

    
      </div>

      {isLoading ? (
        <div className={styles.loadingContainer}>
          <p>Loading short stories...</p>
        </div>
      ) : (
        <Swiper
          modules={[Mousewheel, FreeMode]}
          freeMode={{
            enabled: true,
            momentum: true,
            momentumRatio: 1, // smoother momentum
            momentumVelocityRatio: 1, // normal velocity handling
            minimumVelocity: 0.02, // keep this small to allow tiny moves
          }}
          mousewheel={{
            forceToAxis: true,
            sensitivity: 1.5,
            releaseOnEdges: true,
          }}
          speed={400}
          resistance={true} // Allow a bit of resistance (helps feel natural)
          resistanceRatio={0.85}
          spaceBetween={10}
          className={styles.swiper}
          breakpoints={{
            320: { slidesPerView: 3, spaceBetween: 8 },
            480: { slidesPerView: 3, spaceBetween: 10 },
            640: { slidesPerView: 4, spaceBetween: 10 },
            768: { slidesPerView: 5, spaceBetween: 10 },
            1024: { slidesPerView: 7, spaceBetween: 10 },
            1440: { slidesPerView: 7, spaceBetween: 12 },
          }}
        >
          {displayData && displayData.length > 0 ? (
            displayData.map((item) => (
              <SwiperSlide key={item.id} className={styles.slide}>
                <div className={styles.card}>
                  <div className={styles.imageWrapper}>
                    <img
                      src={item.computer_image_url}
                      alt={item.title}
                      className={styles.image}
                      onClick={() => navigate(`/audio-play/${item.slug}`)}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/default-image.jpg"; // Fallback image
                      }}
                      loading="lazy"
                    />
                 
                  </div>
                  <div className={styles.info}>
                    <div className={styles.duration}>
                      <span>
                        <Clock size={15} className={styles.durationIcon} />{" "}
                        {formatDuration(item.duration)}
                      </span>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))
          ) : (
            <SwiperSlide>
              <div
                className={styles.noContentMessage}
                style={{ color: "white" }}
              >
                No short stories available for this category.
              </div>
            </SwiperSlide>
          )}
        </Swiper>
      )}
    </div>
  );
};

export default AudioBookCarousel;